import React, { useState } from "react";
import {
  Box,
  Text,
  Stack,
  Flex,
  Button,
  Radio,
  RadioGroup,
  useToast,
  Icon,
  Divider,
} from "@chakra-ui/react";
import { FaUserSecret, FaUser } from "react-icons/fa";
import dayjs from "dayjs";

const PollScreen = ({ space, admin, member, referenceId, activities }) => {
  const pollQuestion = space?.metaData?.question || "Unknown Poll";
  const pollOptions = space?.metaData?.options || [];
  const [selected, setSelected] = useState("");
  const [hasVoted, setHasVoted] = useState(false);
  const toast = useToast();

  // Determine if the member has already voted and what option
  let memberVotedOption = null;
  if (
    member?.activity?.activityType === "pollVote" &&
    member.activity.pollOptionId
  ) {
    memberVotedOption = member.activity.pollOptionId;
  }

  // If member has already voted, set selected and hasVoted accordingly
  React.useEffect(() => {
    if (memberVotedOption) {
      setSelected(memberVotedOption);
      setHasVoted(true);
    }
  }, [memberVotedOption]);

  const handleVote = async () => {
    if (!selected) {
      toast({
        title: "Please select an option",
        status: "warning",
        duration: 2000,
        isClosable: true,
      });
      return;
    }
    try {
      // Call createActivity API for poll vote
      const res = await fetch(`/api/spaces/${referenceId}/activity`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: member?.guestAccessId
            ? `Guest ${member.guestAccessId}`
            : undefined,
        },
        body: JSON.stringify({
          activityType: "pollVote",
          pollOptionId: selected,
        }),
      });
      if (!res.ok) throw new Error("Failed to submit vote");
      // Vote submitted successfully
      setHasVoted(true);
    } catch (err) {
      toast({
        title: "Failed to submit vote",
        status: "error",
        duration: 3000,
        isClosable: true,
      });
    }
  };

  // Use activities.votes from API if available
  let pollResults = [];
  if (activities && Array.isArray(activities.votes)) {
    pollResults = activities.votes;
  }

  let percentages = [];
  let maxPercent = 0;
  let minPercent = 0;
  let totalVotes = 0;
  if (hasVoted && pollResults.length > 0) {
    totalVotes = pollResults.reduce((sum, v) => sum + v.count, 0) || 1;
    percentages = pollOptions.map((opt) => {
      const found = pollResults.find((r) => r.optionId === opt.id);
      const count = found ? found.count : 0;
      return {
        id: opt.id,
        percent: (count / totalVotes) * 100,
        count,
      };
    });
    maxPercent = Math.max(...percentages.map((p) => p.percent));
    minPercent = Math.min(...percentages.map((p) => p.percent));
  }

  return (
    <Flex
      direction="column"
      align="center"
      minH="100vh"
      bgGradient="linear(to-br, teal.600, white, teal.50)"
    >
      {/* Content area */}
      <Flex direction="column" align="center" flex="1" w="100%" p={4}>
        {/* Header section */}
        <Box
          w="100%"
          maxW="lg"
          p={4}
          bg="white"
          borderRadius="lg"
          boxShadow="sm"
          borderWidth="1px"
        >
          {/* Poll title and description */}
          <Box textAlign="left">
            <Text fontWeight="bold" fontSize="2xl" mb={2}>
              {space?.title || "Poll"}
            </Text>
            <Text fontSize="sm" color="gray.600" pb={2}>
              {space?.description ||
                "Please select your option below. You can only vote once."}
            </Text>
            <Divider />
          </Box>

          {/* Member info after divider */}
          <Flex justify="flex-start" mt={4}>
            <Flex align="center">
              <Icon as={FaUser} color="gray.500" mr={2} />
              <Text
                fontWeight="medium"
                fontSize="sm"
                color="gray.700"
                noOfLines={1}
              >
                {member?.name || member?.alias || "You"}
              </Text>
            </Flex>
          </Flex>
        </Box>

        {/* Poll container */}
        <Box
          p={8}
          mt={4}
          maxW="lg"
          w="100%"
          bg="white"
          borderWidth="1px"
          borderRadius="lg"
          boxShadow="md"
        >
          {/* Poll Question */}
          <Text fontSize="lg" fontWeight="semibold" color="gray.800" mb={6}>
            {pollQuestion}
          </Text>

          {/* Pre-vote: show form */}
          {!hasVoted ? (
            <>
              <RadioGroup onChange={setSelected} value={selected} mb={6}>
                <Stack spacing={4}>
                  {pollOptions.map((opt) => (
                    <Radio
                      key={opt.id}
                      value={opt.id}
                      size="lg"
                      isDisabled={!!memberVotedOption}
                    >
                      {opt.label}
                    </Radio>
                  ))}
                </Stack>
              </RadioGroup>
              <Button
                colorScheme="teal"
                onClick={handleVote}
                w="full"
                isDisabled={!selected || !!memberVotedOption}
              >
                Vote
              </Button>
            </>
          ) : (
            /* Post-vote: show results */
            <Stack spacing={4}>
              {pollOptions.map((opt) => {
                const pctEntry = percentages.find((p) => p.id === opt.id) || {
                  percent: 0,
                  count: 0,
                };
                const pct = Math.round(pctEntry.percent);
                let hue = 0;
                if (maxPercent !== minPercent) {
                  hue =
                    120 *
                    ((pctEntry.percent - minPercent) /
                      (maxPercent - minPercent));
                } else {
                  hue = 120;
                }
                const gradientBar = `hsl(${hue}, 70%, 50%)`;
                return (
                  <Box key={opt.id} w="100%">
                    <Flex align="center" justify="space-between" mb={1}>
                      <Text>{opt.label}</Text>
                      <Text fontSize="sm">{pct}%</Text>
                    </Flex>
                    <Box
                      w="100%"
                      bg="gray.200"
                      borderRadius="md"
                      overflow="hidden"
                      mb={4}
                      h="8px"
                    >
                      <Box w={`${pct}%`} h="8px" bg={gradientBar} />
                    </Box>
                  </Box>
                );
              })}
              <Text textAlign="center" color="green.500">
                Thank you for voting!
              </Text>
              <Text textAlign="center" color="gray.600" fontSize="sm">
                Total votes: {totalVotes}
              </Text>
            </Stack>
          )}
        </Box>

        {/* Admin info */}
        <Box mt={4} textAlign="center">
          <Text
            fontSize="sm"
            color="gray.600"
            display="flex"
            alignItems="center"
            justifyContent="center"
          >
            Created by {admin?.name || admin?.alias || "Unknown User"}
            <Icon
              as={space.isIncognito ? FaUserSecret : FaUser}
              ml={2}
              title={space.isIncognito ? "Incognito" : "Admin"}
            />
          </Text>
          <Text fontSize="xs" color="gray.500">
            {space.createdAt
              ? dayjs(space.createdAt).format("MMM D, YYYY h:mm A")
              : ""}
          </Text>
        </Box>
      </Flex>

      {/* Footer - Always at bottom */}
      <Box w="100%" textAlign="center" py={4}>
        <Text fontSize="xs" color="gray.500" mb={2}>
          This content is neither created nor endorsed by Vartaverse
        </Text>
        <Text
          fontSize="lg"
          fontWeight="bold"
          color="teal.600"
          cursor="pointer"
          _hover={{
            color: "teal.700",
            textDecoration: "none",
          }}
          onClick={() => window.open(window.location.origin, "_blank")}
        >
          VartaVerse
        </Text>
      </Box>
    </Flex>
  );
};

export default PollScreen;
